"use client";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import IncomeChart from "./../../IncomeChart/IncomeChart";
import NotificationPanel from "./../../Notifications/Notifications";
import NewsUpdates from "../../NewsCard/NewsCard";
import Calendar from "../../Calendar/Calendar";
import calendar_white from "../../assets/calendar_white.png";
import clients_white from "../../assets/clients_white.png";
import analytics_white from "../../assets/analytics_white.png";
import g from "../../assets/gif.gif";
import { REST_API_URL } from "../../api/config";
import { getPaymentCheckThunk } from "../../redux/slices/paymentSlice/asyncthunk";
import {
  getUserViewThunk,
  getUserCurrencyThunk,
  getIndustryThunk,
  getStoriesThunk,
} from "../../redux/slices/profileSlice/asyncthunk";
import { useAppDispatch, useAppSelector } from "../../hooks/redux/useRedux";
import { getNotificationListThunk } from "../../redux/slices/notificationSlice/asyncthunk";
import { selectNotification } from "../../redux/slices/notificationSlice/NotificationSlice";
import { buildHeaders } from "../../api/buildHeaders";
import { getCurrencySymbol } from "../../utils/currency/getCurrencySymbol";
import { AffiliationModal } from "../../components/modals/affiliationModal";
import { getAffiliation } from "../../api/profile/profileServices";

const API_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const getToken = () =>
  localStorage.getItem("accessToken") ||
  sessionStorage.getItem("accessToken") ||
  localStorage.getItem("token") ||
  sessionStorage.getItem("token");

const clearAuthStorage = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user_status");
  localStorage.removeItem("halper_remember_me");
  localStorage.removeItem("halper_saved_email");
  localStorage.removeItem("last_login_time");
  localStorage.removeItem("login_timezone");
  localStorage.removeItem("analyticsCurrency");
  localStorage.removeItem("analyticsCurrencyTimestamp");

  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem("refreshToken");
  sessionStorage.removeItem("user_status");
  sessionStorage.removeItem("last_login_time");
  sessionStorage.removeItem("login_timezone");
};

const fetchUserCurrency = async (token) => {
  try {
    if (!token) {
      return { currency: "USD" };
    }

    const headers = buildHeaders();

    const res = await fetch(`${API_URL}/service/user-currency/`, {
      headers,
      cache: "no-store",
    });

    const responseData = await res.json().catch(() => ({}));

    if (!res.ok) {
      return { currency: "USD" };
    }

    const resolvedCurrency =
      responseData?.currency ||
      responseData?.user_currency ||
      responseData?.data?.currency ||
      "USD";

    return { currency: resolvedCurrency };
  } catch (error) {
    console.error("Error fetching user currency:", error);
    return { currency: "USD" };
  }
};

function Card({
  title,
  subtitle,
  growth,
  icon,
  showCurrencySymbol = false,
  currencySymbol = "$",
}) {
  return (
    <div className="rounded-lg bg-white shadow p-6 flex justify-between items-start">
      <div className="flex flex-col gap-5">
        <div className="h-8 w-8 flex items-center justify-center rounded-full bg-[#020637]">
          {showCurrencySymbol ? (
            <span className="text-white text-sm font-medium">
              {currencySymbol}
            </span>
          ) : (
            <img
              src={icon}
              alt={subtitle}
              className="max-w-[16px] max-h-[16px]"
            />
          )}
        </div>
        <div>
          <p className="text-xl font-semibold">{title}</p>
          <p className="text-sm text-neutral-500">{subtitle}</p>
        </div>
      </div>

      {growth && (
        <span
          className={`text-sm font-medium ${
            String(growth).startsWith("-") ? "text-red-500" : "text-green-500"
          }`}
        >
          {growth}
        </span>
      )}
    </div>
  );
}

export default function Home() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [stats, setStats] = useState({
    totalRevenue: "0.00",
    revenueGrowth: "0%",
    appointmentsToday: "0",
    newCustomers: "0",
    currencySymbol: "$",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [affiliationData, setAffiliationData] = useState(null);

  const userTz = useMemo(() => getUserTimeZone(), []);

  const notificationList = useAppSelector(selectNotification);

  // Appointments today, derived from the single notifications fetch in the store.
  useEffect(() => {
    const all = [
      ...(notificationList?.new || []),
      ...(notificationList?.earlier || []),
    ];
    const today = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      timeZone: userTz,
    });
    const count = all.filter((n) => n.appointment_date === today).length;
    setStats((prev) => ({ ...prev, appointmentsToday: String(count) }));
  }, [notificationList, userTz]);

  const getAffiliationData = useCallback(() => {
    const isViewed = localStorage.getItem("affiliationData");
    if (!isViewed) {
      getAffiliation()
        .then((aff) => {
          if (aff?.data) {
            setAffiliationData(aff.data);
          }
        })
        .catch((err) => {
          console.error("Error fetching affiliation data:", err);
        });
    }
  }, []);

  const fetchAllData = async () => {
    dispatch(getPaymentCheckThunk());
    dispatch(getUserViewThunk());
    dispatch(getUserCurrencyThunk());
    dispatch(getIndustryThunk());
    dispatch(getStoriesThunk());
    dispatch(getNotificationListThunk());
    getAffiliationData();

    const token = getToken();

    if (!token) {
      setError(t("home.noTokenError"));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const userCurrencyData = await fetchUserCurrency(token);
      const currencySymbol = getCurrencySymbol(userCurrencyData?.currency);

      const headers = buildHeaders();

      const analyticsRes = await fetch(
        `${API_URL}/analytics/analytics-veriables/`,
        {
          headers,
          cache: "no-store",
        },
      );

      if (analyticsRes.status === 401) {
        clearAuthStorage();
        setError(t("home.sessionExpiredError"));
        return;
      }

      const analyticsData = await analyticsRes.json().catch(() => ({}));

      setStats((prev) => ({
        ...prev,
        totalRevenue: analyticsData.clients_lifetime_value || "0.00",
        revenueGrowth:
          analyticsData.clients_lifetime_value_growth_percentage || "0%",
        newCustomers: analyticsData.clients_count || "0",
        currencySymbol,
      }));
    } catch (err) {
      console.error("❌ Error fetching dashboard data:", err);
      setError(t("home.analyticsLoadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();

    const handler = (e) => {
      if (e.detail?.add) {
        setStats((prev) => ({
          ...prev,
          appointmentsToday: String(
            Number(prev.appointmentsToday || 0) + Number(e.detail.add || 0),
          ),
        }));
      }

      setTimeout(fetchAllData, 1000);
    };

    window.addEventListener("refreshHomeStats", handler);
    return () => window.removeEventListener("refreshHomeStats", handler);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <img
          src={g}
          alt={t("home.loadingAlt")}
          style={{ maxWidth: "300px" }}
          className="loader-ed-image"
        />
      </div>
    );
  }

  if (error) {
    return (
      <main className="flex flex-1 items-center justify-center">
        <p className="text-red-500">{error}</p>
      </main>
    );
  }

  return (
    <main className="flex flex-1 flex-col overflow-auto p-4 gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card
          title={`${stats.currencySymbol}${stats.totalRevenue}`}
          subtitle={t("home.totalRevenue")}
          growth={stats.revenueGrowth}
          showCurrencySymbol
          currencySymbol={stats.currencySymbol}
        />
        <Card
          title={stats.appointmentsToday}
          subtitle={t("home.appointmentsToday")}
          icon={calendar_white}
        />
        <Card
          title={stats.newCustomers}
          subtitle={t("home.newCustomers")}
          icon={clients_white}
        />
        <Card
          title={stats.revenueGrowth}
          subtitle={t("home.revenueGrowth")}
          growth={stats.revenueGrowth}
          icon={analytics_white}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div className="rounded-lg bg-white shadow h-full">
            <IncomeChart />
          </div>
        </div>
        <div className="h-[420px]">
          <div className="rounded-lg bg-white shadow h-full">
            <NewsUpdates />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 rounded-xl">
        <div className="xl:col-span-2 rounded-xl">
          <div
            className="bg-white rounded-xl shadow"
            style={{ borderRadius: "21px" }}
          >
            <Calendar />
          </div>
        </div>
        <div>
          <div
            className="bg-white rounded-xl shadow height-full-not"
            style={{ maxHeight: "780px", overflowY: "auto" }}
          >
            <NotificationPanel />
          </div>
        </div>
      </div>

      {/* {affiliationData && (
        <AffiliationModal
          visible={affiliationData !== null}
          data={affiliationData}
          onClose={() => {
            localStorage.setItem("affiliationData", "viewed");
            setAffiliationData(null);
          }}
        />
      )} */}
    </main>
  );
}
